import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CommissionTierInput,
  CreateProductInput,
  CreateSupplierInput,
  SupplierApiConfigInput,
  SupplierFieldMappingInput,
  UpdateProductInput,
  UpdateSupplierInput,
} from '@aggregator/shared';
import { api } from './client';
import type {
  AdminOrderDetail,
  AdminProductDetail,
  AdminProductListItem,
  SupplierRecoveryOrder,
  AdminSettings,
  AdminSupplier,
  AdminWalletTransaction,
  CommissionResponse,
  DashboardStats,
  Paged,
  RevenuePoint,
  SupplierAccountResult,
  SupplierProbeResult,
  SyncResult,
  AdminOrderListItem,
  AdminUsersResponse,
} from './types';

export function useUsers(search = '') {
  return useQuery({
    queryKey: ['users', search],
    queryFn: async () => (await api.get<AdminUsersResponse>('/users', { params: search ? { search } : {} })).data,
  });
}

// ── Dashboard ──────────────────────────────────────────────

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get<DashboardStats>('/stats/dashboard')).data,
    refetchInterval: 30_000,
  });
}

export function useRevenue(days = 30) {
  return useQuery({
    queryKey: ['revenue', days],
    queryFn: async () => (await api.get<{ points: RevenuePoint[] }>('/stats/revenue', { params: { days } })).data.points,
  });
}

// ── Suppliers ──────────────────────────────────────────────

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await api.get<AdminSupplier[]>('/suppliers')).data,
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: ['supplier', id],
    queryFn: async () => (await api.get<AdminSupplier>(`/suppliers/${id}`)).data,
    enabled: Boolean(id),
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSupplierInput) => (await api.post<AdminSupplier>('/suppliers', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSupplierInput) => (await api.put<AdminSupplier>(`/suppliers/${id}`, input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['supplier', id] });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete<{ deletedProducts: number }>(`/suppliers/${id}`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/** Fetch a sample response + auto-match for a SAVED supplier. */
export function useProbeSupplier(id: string) {
  return useMutation({
    mutationFn: async () => (await api.get<SupplierProbeResult>(`/suppliers/${id}/probe`)).data,
  });
}

/** Fetch a sample response + auto-match for an UNSAVED inline config. */
export function useProbeSupplierInline() {
  return useMutation({
    mutationFn: async (payload: {
      apiConfig: SupplierApiConfigInput;
      fieldMappings?: SupplierFieldMappingInput[];
    }) => (await api.post<SupplierProbeResult>('/suppliers/probe', payload)).data,
  });
}

export function useSyncSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<SyncResult>(`/suppliers/${id}/sync`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['supplier', id] });
      void qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useRefreshBalance(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.get<{ balance: number }>(`/suppliers/${id}/balance`)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['supplier', id] }),
  });
}

/** Supplier account profile — balance + recent ledger (the supplier's /me or /balance). */
export function useSupplierAccount(id: string) {
  return useMutation({
    mutationFn: async () => (await api.get<SupplierAccountResult>(`/suppliers/${id}/account`)).data,
  });
}

// ── Products ───────────────────────────────────────────────

export function useProducts(params: { page: number; pageSize: number; search?: string; supplierId?: string }) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: async () => (await api.get<Paged<AdminProductListItem>>('/products', { params })).data,
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async () => (await api.get<AdminProductDetail>(`/products/${id}`)).data,
    enabled: Boolean(id),
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProductInput) => (await api.post<AdminProductDetail>('/products', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateProductInput) => (await api.put<AdminProductDetail>(`/products/${id}`, input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['product', id] });
    },
  });
}

export function useSetManualPrice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pricingOverride: number | null) =>
      (await api.patch<AdminProductDetail>(`/products/${id}/price`, { pricingOverride })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['product', id] });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/products/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

// ── Orders ─────────────────────────────────────────────────

export interface OrderFilters {
  status?: string;
  supplierId?: string;
  search?: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

export function useOrders(filters: OrderFilters) {
  return useQuery({
    queryKey: ['orders', filters],
    queryFn: async () => {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v != null));
      return (await api.get<Paged<AdminOrderListItem>>('/orders', { params })).data;
    },
  });
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: async () => (await api.get<AdminOrderDetail>(`/orders/${id}`)).data,
    enabled: Boolean(id),
  });
}

export function useRefundOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (await api.patch(`/orders/${id}/refund`, { note })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useFetchRecoveryOrders() {
  return useMutation({
    mutationFn: async (id: string) => (await api.get<{ items: SupplierRecoveryOrder[] }>(`/orders/${id}/recovery-orders`)).data.items,
  });
}

export function useAssignManualDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, supplierOrderId }: { id: string; supplierOrderId: string }) =>
      (await api.post(`/orders/${id}/manual-delivery`, { supplierOrderId })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['order'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useApproveOrderPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post(`/orders/${id}/payment/approve`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useRejectOrderPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (await api.post(`/orders/${id}/payment/reject`, { note })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ── Commission tiers ───────────────────────────────────────

export function useCommissionTiers() {
  return useQuery({
    queryKey: ['commission'],
    queryFn: async () => (await api.get<CommissionResponse>('/commission-tiers')).data,
  });
}

export function useCreateTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CommissionTierInput) => (await api.post('/commission-tiers', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['commission'] }),
  });
}

export function useUpdateTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<CommissionTierInput> }) =>
      (await api.put(`/commission-tiers/${id}`, input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['commission'] }),
  });
}

export function useDeleteTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/commission-tiers/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['commission'] }),
  });
}

// ── Settings ───────────────────────────────────────────────

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get<AdminSettings>('/settings')).data,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<AdminSettings>) => (await api.put<AdminSettings>('/settings', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

// ── Wallet (user top-ups) ──────────────────────────────────

export interface WalletTxFilters {
  status?: string;
  telegramUserId?: number;
  page: number;
  pageSize: number;
}

export function useWalletTransactions(filters: WalletTxFilters) {
  return useQuery({
    queryKey: ['wallet-transactions', filters],
    queryFn: async () => {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v != null));
      return (await api.get<Paged<AdminWalletTransaction>>('/wallet/transactions', { params })).data;
    },
  });
}

export function useApproveTopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (await api.post<{ ok: true; newBalance: number }>(`/wallet/transactions/${id}/approve`, { note })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['wallet-transactions'] }),
  });
}

export function useRejectTopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (await api.post(`/wallet/transactions/${id}/reject`, { note })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['wallet-transactions'] }),
  });
}

export function useAdjustWalletBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { telegramUserId: number; amountUsdt: number; note: string }) =>
      (await api.post<{ ok: true; newBalance: number }>('/wallet/adjust', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['wallet-transactions'] }),
  });
}

// ── Field-mapper preview ───────────────────────────────────

export function usePreviewMappings() {
  return useMutation({
    mutationFn: async (payload: { sample: unknown; mappings: unknown }) =>
      (await api.post<{ preview: Array<{ ourField: string; supplierPath: string; resolved: unknown; transformed: unknown }> }>(
        '/suppliers/mappings/preview',
        payload,
      )).data.preview,
  });
}
